import React from 'react';
import { motion } from 'framer-motion';
import * as RechartsPrimitive from 'recharts';
import {
  BarChart, CartesianGrid, LineChart, Line, XAxis, YAxis, Bar,
  PieChart, Pie, AreaChart, Area, ScatterChart, Scatter,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import '../../Styles/Visualizations.css';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '../ui/chart';

interface VisualizationProps {
  subtype: string;
  data: any;
  config: any;
  preview?: boolean;
  isStreaming?: boolean;
  collapsed?: boolean;
}

const CHART_COLORS = Array.from({ length: 10 }, (_, i) => `hsl(var(--chart-${i + 1}))`);

const getColor = (index: number) => CHART_COLORS[index % CHART_COLORS.length];

const renderCartesianChart = (
  ChartComponent: any,
  specificElements: React.ReactNode,
  data: any,
  config: any,
  additionalProps: Record<string, any> = {}
) => (
  <ChartComponent data={data} accessibilityLayer {...additionalProps}>
    <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />
    <XAxis
      dataKey={config.xField || 'name'}
      label={config.xAxis ? { value: config.xAxis, position: 'bottom', offset: 0, style: { fontSize: 12, fill: 'var(--text-secondary)' } } : undefined}
      tickLine={false}
      tickMargin={10}
      axisLine={false}
      style={{ fontSize: 11 }}
    />
    <YAxis
      label={config.yAxis ? { value: config.yAxis, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: 'var(--text-secondary)' } } : undefined}
      tickLine={false}
      axisLine={false}
      style={{ fontSize: 11 }}
    />
    <ChartTooltip
      cursor={{ stroke: 'var(--text-tertiary)', strokeWidth: 1, strokeDasharray: '4 4' }}
      content={<ChartTooltipContent
        className="tooltip-custom"
        indicator={ChartComponent === BarChart ? "dashed" : "dot"}
      />}
    />
    {specificElements}
  </ChartComponent>
);

const getChartAndConfig = (subtype: string, data: any, config: any, animate: boolean = true): { chartConfig: any; chart: React.ReactNode | null } => {
  switch (subtype) {
    // ─── LINE CHART ──────────────────────────────────────────
    case 'line': {
      const yFields = config.yFields || [config.yField || 'value'];
      const elements = yFields.map((field: string, i: number) => (
        <Line
          key={field}
          dataKey={field}
          type="monotone"
          stroke={getColor(i)}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0, fill: getColor(i) }}
          isAnimationActive={animate}
        />
      ));
      const chartConfig: any = {};
      yFields.forEach((field: string, i: number) => {
        chartConfig[field] = { label: config.labels?.[field] || field, color: getColor(i) };
      });
      return {
        chartConfig,
        chart: renderCartesianChart(LineChart, elements, data, config, { margin: { left: 12, right: 12, bottom: 20, top: 8 } }),
      };
    }

    // ─── AREA CHART ──────────────────────────────────────────
    case 'area': {
      const yFields = config.yFields || [config.yField || 'value'];
      const stacked = config.stacked !== false;
      const elements = yFields.map((field: string, i: number) => (
        <Area
          key={field}
          dataKey={field}
          type="monotone"
          stroke={getColor(i)}
          fill={getColor(i)}
          fillOpacity={0.15}
          strokeWidth={2}
          stackId={stacked ? 'stack' : undefined}
          activeDot={{ r: 4, strokeWidth: 0 }}
          isAnimationActive={animate}
        />
      ));
      const chartConfig: any = {};
      yFields.forEach((field: string, i: number) => {
        chartConfig[field] = { label: config.labels?.[field] || field, color: getColor(i) };
      });
      return {
        chartConfig,
        chart: renderCartesianChart(AreaChart, elements, data, config, { margin: { left: 12, right: 12, bottom: 20, top: 8 } }),
      };
    }

    // ─── BAR CHART ───────────────────────────────────────────
    case 'bar': {
      const yFields = config.yFields || [config.yField || 'value'];
      const stacked = config.stacked === true;
      const elements = yFields.map((field: string, i: number) => (
        <Bar
          key={field}
          dataKey={field}
          fill={getColor(i)}
          radius={stacked ? (i === yFields.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]) : [4, 4, 0, 0]}
          stackId={stacked ? 'stack' : undefined}
          maxBarSize={60}
          isAnimationActive={animate}
        />
      ));
      const chartConfig: any = {};
      yFields.forEach((field: string, i: number) => {
        chartConfig[field] = { label: config.labels?.[field] || field, color: getColor(i) };
      });
      return {
        chartConfig,
        chart: renderCartesianChart(BarChart, elements, data, config, { margin: { left: 12, right: 12, bottom: 20, top: 8 } }),
      };
    }

    // ─── HORIZONTAL BAR CHART ────────────────────────────────
    case 'horizontalBar': {
      const yField = config.yField || 'value';
      const xField = config.xField || 'name';
      const chartConfig: any = {};
      chartConfig[yField] = { label: config.labels?.[yField] || yField, color: getColor(0) };
      return {
        chartConfig,
        chart: (
          <BarChart data={data} layout="vertical" accessibilityLayer margin={{ left: 80, right: 20, top: 8, bottom: 8 }}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tickLine={false} axisLine={false} style={{ fontSize: 11 }} />
            <YAxis type="category" dataKey={xField} tickLine={false} axisLine={false} width={70} style={{ fontSize: 11 }} />
            <ChartTooltip content={<ChartTooltipContent className="tooltip-custom" indicator="dashed" />} />
            <Bar dataKey={yField} fill={getColor(0)} radius={[0, 4, 4, 0]} maxBarSize={40} isAnimationActive={animate} />
          </BarChart>
        ),
      };
    }

    // ─── PIE / DONUT CHART ───────────────────────────────────
    case 'pie':
    case 'donut': {
      const dataKey = config.yField || 'value';
      const nameKey = config.xField || 'name';
      const isDonut = subtype === 'donut' || config.innerRadius;
      const innerRadius = isDonut ? (config.innerRadius || 60) : 0;
      const strokeWidth = config.strokeWidth || 2;

      const pieChartConfig: any = {};
      data.forEach((entry: any, index: number) => {
        const key = entry[nameKey];
        if (key) {
          pieChartConfig[key] = { label: key, color: getColor(index) };
        }
      });
      if (dataKey) {
        pieChartConfig[dataKey] = { label: config.label || dataKey };
      }

      const total = data.reduce((acc: number, cur: any) => acc + (cur[dataKey] || 0), 0);

      return {
        chartConfig: pieChartConfig,
        chart: (
          <PieChart>
            <ChartTooltip content={<ChartTooltipContent className="tooltip-custom" />} />
            <Pie
              data={data}
              dataKey={dataKey}
              nameKey={nameKey}
              innerRadius={innerRadius}
              strokeWidth={strokeWidth}
              paddingAngle={data.length > 1 ? 2 : 0}
              isAnimationActive={animate}
            >
              {data.map((_: any, index: number) => (
                <RechartsPrimitive.Cell
                  key={`cell-${index}`}
                  fill={getColor(index)}
                />
              ))}
              {isDonut && (
                <RechartsPrimitive.Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan x={viewBox.cx} y={viewBox.cy} className="fill-[var(--text-primary)] text-3xl font-bold">
                            {total.toLocaleString()}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground text-xs">
                            {config.label || "Total"}
                          </tspan>
                        </text>
                      );
                    }
                    return null;
                  }}
                />
              )}
            </Pie>
            <ChartLegend content={<ChartLegendContent />} />
          </PieChart>
        ),
      };
    }

    // ─── SCATTER CHART ───────────────────────────────────────
    case 'scatter': {
      const xField = config.xField || 'x';
      const yField = config.yField || 'y';
      const zField = config.zField;

      const groups = config.groups
        ? [...new Set(data.map((d: any) => d[config.groups]))]
        : [null];

      const chartConfig: any = {};
      groups.forEach((group: any, i: number) => {
        const key = group || yField;
        chartConfig[key] = { label: group || config.labels?.[yField] || yField, color: getColor(i) };
      });

      return {
        chartConfig,
        chart: (
          <ScatterChart accessibilityLayer margin={{ left: 12, right: 12, bottom: 20, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              type="number"
              dataKey={xField}
              name={config.xAxis || xField}
              tickLine={false}
              axisLine={false}
              style={{ fontSize: 11 }}
              label={config.xAxis ? { value: config.xAxis, position: 'bottom', style: { fontSize: 12, fill: 'var(--text-secondary)' } } : undefined}
            />
            <YAxis
              type="number"
              dataKey={yField}
              name={config.yAxis || yField}
              tickLine={false}
              axisLine={false}
              style={{ fontSize: 11 }}
              label={config.yAxis ? { value: config.yAxis, angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'var(--text-secondary)' } } : undefined}
            />
            <ChartTooltip content={<ChartTooltipContent className="tooltip-custom" />} />
            {groups.map((group: any, i: number) => {
              const filteredData = group ? data.filter((d: any) => d[config.groups] === group) : data;
              return (
                <Scatter
                  key={group || 'default'}
                  name={group || config.labels?.[yField] || yField}
                  data={filteredData}
                  fill={getColor(i)}
                  isAnimationActive={animate}
                >
                  {zField && filteredData.map((_: any, idx: number) => (
                    <RechartsPrimitive.Cell
                      key={idx}
                      r={Math.max(4, Math.min(20, (filteredData[idx][zField] || 6)))}
                    />
                  ))}
                </Scatter>
              );
            })}
          </ScatterChart>
        ),
      };
    }

    // ─── RADAR CHART ─────────────────────────────────────────
    case 'radar': {
      const axisField = config.xField || 'subject';
      const valueFields = config.yFields || [config.yField || 'value'];

      const chartConfig: any = {};
      valueFields.forEach((field: string, i: number) => {
        chartConfig[field] = { label: config.labels?.[field] || field, color: getColor(i) };
      });

      return {
        chartConfig,
        chart: (
          <RadarChart data={data} cx="50%" cy="50%" outerRadius="75%" margin={{ top: 8, bottom: 8 }}>
            <PolarGrid stroke="var(--border-interactive)" opacity={0.5} />
            <PolarAngleAxis dataKey={axisField} style={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
            <PolarRadiusAxis style={{ fontSize: 10 }} axisLine={false} tick={false} />
            <ChartTooltip content={<ChartTooltipContent className="tooltip-custom" />} />
            {valueFields.map((field: string, i: number) => (
              <Radar
                key={field}
                name={config.labels?.[field] || field}
                dataKey={field}
                stroke={getColor(i)}
                fill={getColor(i)}
                fillOpacity={0.15}
                strokeWidth={2}
                isAnimationActive={animate}
              />
            ))}
            {valueFields.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
          </RadarChart>
        ),
      };
    }

    // ─── UNSUPPORTED ─────────────────────────────────────────
    default:
      return { chartConfig: null, chart: null };
  }
};

const Visualization: React.FC<VisualizationProps> = ({
  subtype,
  data,
  config,
  preview = false,
  isStreaming = false,
  collapsed = false,
}) => {
  const { chartConfig, chart } = getChartAndConfig(subtype, data, config, !isStreaming);

  if (!chart) {
    return (
      <div className="viz-unsupported">
        <span>Unsupported visualization type: {subtype}</span>
      </div>
    );
  }

  if (preview) {
    return (
      <ChartContainer config={chartConfig}>
        {chart as React.ReactElement}
      </ChartContainer>
    );
  }

  return (
    <div className="viz-card">
      {(config.title || config.description) && (
        <div className="viz-card-header">
          {config.title && <p className="viz-card-title">{config.title}</p>}
          {config.description && <p className="viz-card-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className="viz-card-content">
          <ChartContainer config={chartConfig}>
            {chart as React.ReactElement}
          </ChartContainer>
        </div>
        {config.explanation && (
          <div className="viz-card-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Visualization;
